const PromptTemplateLLM = ({
  llmProviders,
  sections,
  onSectionChange,
}) => {
  return (
    <div className="record-form-fields-container">
      <div className="input-control separate-label">
        <label>LLM Configuration</label>
        <div className="input-help-text">
          Configure model selection for each section of this template.
        </div>
      </div>

      <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {sections.map((section) => {
          const sectionModels = section.models || [];
          const selectedProvider = section.selectedProvider || '';
          const fallbackProvider = section.fallbackProvider || '';
          const providersForSection = llmProviders.filter((provider) =>
            sectionModels.some((model) => model.providerId === provider.id)
          );
          return (
            <div key={section.id} style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fafafa' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>{section.label}</div>

              <div className="input-control separate-label">
                <label htmlFor={`${section.id}-provider`}>Provider</label>
                <select
                  id={`${section.id}-provider`}
                  value={selectedProvider}
                  onChange={(e) => onSectionChange(section.id, { selectedProvider: e.target.value })}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  <option value="">Select a provider...</option>
                  {providersForSection.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-control separate-label">
                <label htmlFor={`${section.id}-model`}>Model</label>
                <select
                  id={`${section.id}-model`}
                  value={section.selectedModel || ''}
                  onChange={(e) => onSectionChange(section.id, { selectedModel: e.target.value })}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  <option value="">Select a model...</option>
                  {sectionModels
                    .filter((m) => !selectedProvider || m.providerId === selectedProvider)
                    .map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} {model.maxInputTokens ? `(${Math.round(model.maxInputTokens / 1000)}K context)` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div className="input-control separate-label">
                <label htmlFor={`${section.id}-fallback-provider`}>Fallback Provider</label>
                <select
                  id={`${section.id}-fallback-provider`}
                  value={fallbackProvider}
                  onChange={(e) => onSectionChange(section.id, { fallbackProvider: e.target.value, fallbackModel: '' })}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  <option value="">None (no fallback)</option>
                  {providersForSection.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                <div className="input-help-text">
                  Used automatically if the primary model call fails.
                </div>
              </div>

              <div className="input-control separate-label">
                <label htmlFor={`${section.id}-fallback-model`}>Fallback Model</label>
                <select
                  id={`${section.id}-fallback-model`}
                  value={section.fallbackModel || ''}
                  onChange={(e) => onSectionChange(section.id, { fallbackModel: e.target.value })}
                  disabled={!fallbackProvider}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  <option value="">Select a fallback model...</option>
                  {sectionModels
                    .filter((m) => fallbackProvider && m.providerId === fallbackProvider)
                    .map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} {model.maxInputTokens ? `(${Math.round(model.maxInputTokens / 1000)}K context)` : ''}
                      </option>
                    ))}
                </select>
              </div>

              {section.modelType === 'text' && (
                <>
                  <div className="input-control separate-label">
                    <label htmlFor={`${section.id}-temperature`}>Temperature</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        id={`${section.id}-temperature`}
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={section.llmTemperature ?? 0.7}
                        onChange={(e) => onSectionChange(section.id, { llmTemperature: parseFloat(e.target.value) })}
                        style={{ flex: 1 }}
                      />
                      <span style={{ minWidth: '40px', textAlign: 'right' }}>{section.llmTemperature ?? 0.7}</span>
                    </div>
                    <div className="input-help-text">
                      Higher values make output more random, lower values more deterministic.
                    </div>
                  </div>

                  <div className="input-control separate-label">
                    <label htmlFor={`${section.id}-max-tokens`}>Max Tokens</label>
                    <input
                      id={`${section.id}-max-tokens`}
                      type="number"
                      value={section.llmMaxTokens ?? 4000}
                      onChange={(e) => onSectionChange(section.id, { llmMaxTokens: parseInt(e.target.value, 10) || 4000 })}
                      min={100}
                      max={128000}
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>
                </>
              )}

              <div className="input-control separate-label" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <label style={{ marginBottom: '8px', display: 'block' }}>Current Configuration</label>
                <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                  <div><strong>Provider:</strong> {section.selectedProvider || 'Not selected'}</div>
                  <div><strong>Model:</strong> {section.selectedModel || 'Not selected'}</div>
                  {section.modelType === 'text' && (
                    <>
                      <div><strong>Temperature:</strong> {section.llmTemperature ?? 0.7}</div>
                      <div><strong>Max Tokens:</strong> {section.llmMaxTokens ?? 4000}</div>
                    </>
                  )}
                  <div><strong>Fallback Provider:</strong> {section.fallbackProvider || 'None'}</div>
                  <div><strong>Fallback Model:</strong> {section.fallbackModel || 'None'}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PromptTemplateLLM;
